/**
 * DDY File Fetcher
 *
 * Handles fetching DDY files from weather ZIP archives.
 * DDY files contain design day data in IDF format.
 *
 * External URLs from climate.onebuilding.org are routed through a proxy
 * to bypass CORS restrictions.
 */

import JSZip from 'jszip';
import { parseDdyFull } from './ddyParser';
import type { ParsedDesignDay } from '../types';

/**
 * Check if a URL needs to be proxied due to CORS restrictions.
 */
function needsProxy(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname === 'climate.onebuilding.org';
  } catch {
    return false;
  }
}

/**
 * Get the proxied URL for external weather file downloads.
 */
function getProxiedUrl(url: string): string {
  return `/api/weather-proxy?url=${encodeURIComponent(url)}`;
}

/**
 * Convert an EPW URL to a ZIP URL.
 * Weather files from climate.onebuilding.org are distributed as ZIP archives.
 */
export function epwUrlToZipUrl(epwUrl: string): string {
  // Handle both .epw and already .zip URLs
  if (epwUrl.toLowerCase().endsWith('.zip')) {
    return epwUrl;
  }
  return epwUrl.replace(/\.epw$/i, '.zip');
}

/**
 * Fetch and parse DDY file from a weather ZIP archive.
 *
 * @param zipUrl - URL to the ZIP archive (or EPW URL which will be converted)
 * @returns Parsed design days with full IDF objects
 */
export async function fetchDdyFromZip(zipUrl: string): Promise<ParsedDesignDay[]> {
  // Ensure we have a ZIP URL
  const actualZipUrl = epwUrlToZipUrl(zipUrl);

  // Route external URLs through proxy to bypass CORS
  const fetchUrl = needsProxy(actualZipUrl) ? getProxiedUrl(actualZipUrl) : actualZipUrl;

  const response = await fetch(fetchUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch weather archive: ${response.status} ${response.statusText}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  return extractDdyFromZip(arrayBuffer);
}

/**
 * Extract and parse the DDY file from a ZIP archive.
 *
 * @param zipData - The ZIP file as an ArrayBuffer
 * @returns Parsed design days with full IDF objects
 */
async function extractDdyFromZip(zipData: ArrayBuffer): Promise<ParsedDesignDay[]> {
  const zip = await JSZip.loadAsync(zipData);

  // Find the DDY file in the archive
  const ddyFileName = Object.keys(zip.files).find(
    (name) => name.toLowerCase().endsWith('.ddy')
  );

  if (!ddyFileName) {
    throw new Error('No DDY file found in the ZIP archive');
  }

  const ddyFile = zip.files[ddyFileName];
  if (!ddyFile) {
    throw new Error('Failed to access DDY file in archive');
  }

  const content = await ddyFile.async('string');

  if (!content) {
    throw new Error('Failed to extract DDY file from ZIP archive');
  }

  return parseDdyFull(content);
}

/**
 * Fetch raw DDY content from a ZIP archive without parsing.
 * Useful for displaying raw DDY file content.
 *
 * @param zipUrl - URL to the ZIP archive (or EPW URL which will be converted)
 * @returns Raw DDY file content as string
 */
export async function fetchRawDdyFromZip(zipUrl: string): Promise<string> {
  // Ensure we have a ZIP URL
  const actualZipUrl = epwUrlToZipUrl(zipUrl);

  // Route external URLs through proxy to bypass CORS
  const fetchUrl = needsProxy(actualZipUrl) ? getProxiedUrl(actualZipUrl) : actualZipUrl;

  const response = await fetch(fetchUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch weather archive: ${response.status} ${response.statusText}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Find the DDY file in the archive
  const ddyFileName = Object.keys(zip.files).find(
    (name) => name.toLowerCase().endsWith('.ddy')
  );

  if (!ddyFileName) {
    throw new Error('No DDY file found in the ZIP archive');
  }

  const ddyFile = zip.files[ddyFileName];
  if (!ddyFile) {
    throw new Error('Failed to access DDY file in archive');
  }

  const content = await ddyFile.async('string');

  if (!content) {
    throw new Error('Failed to extract DDY file from ZIP archive');
  }

  return content;
}

/**
 * Check if a ZIP archive contains a DDY file.
 *
 * @param zipUrl - URL to the ZIP archive
 * @returns true if DDY file exists in archive
 */
export async function hasDdyFile(zipUrl: string): Promise<boolean> {
  try {
    const actualZipUrl = epwUrlToZipUrl(zipUrl);
    const fetchUrl = needsProxy(actualZipUrl) ? getProxiedUrl(actualZipUrl) : actualZipUrl;

    const response = await fetch(fetchUrl);
    if (!response.ok) {
      return false;
    }

    const arrayBuffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    return Object.keys(zip.files).some((name) => name.toLowerCase().endsWith('.ddy'));
  } catch {
    return false;
  }
}
